import React, { useState } from "react";
import {
  IoInformationCircleOutline,
  IoMailOutline,
  IoGlobeOutline,
  IoLogoInstagram,
  IoLogoTwitter,
  IoLogoLinkedin,
  IoLogoFacebook,
  IoSendOutline,
  IoChevronBack,
  IoChevronForward,
  IoMusicalNotesOutline,
  IoAddOutline,
} from "react-icons/io5";
import useCategoryTranslation from "../../hooks/useCategoryTranslation";
import { useUserStore } from "../../stores/UserStore";
import { GearPal, GearItem } from "../../types/Pal";
import GearButton from "../../ui/gearbutton";
import GearHeadings from "../../ui/gearheadings";

interface GearPalDetailsProps {
  pal: GearPal;
  onSelectItem: (item: GearItem) => void;
  onAddItem?: () => void;
  onEditItem?: (item: GearItem) => void;
  onDeleteItem?: (id: number) => void;
}

import { DrumIcon } from "../../ui/icons";

const getContactIcon = (type: string) => {
  switch (type.toLowerCase()) {
    case "email":
      return <IoMailOutline className="text-secondary" />;
    case "website":
      return <IoGlobeOutline className="text-secondary" />;
    case "instagram":
      return <IoLogoInstagram className="text-secondary" />;
    case "x":
    case "twitter":
      return <IoLogoTwitter className="text-secondary" />;
    case "linkedin":
      return <IoLogoLinkedin className="text-secondary" />;
    case "facebook":
      return <IoLogoFacebook className="text-secondary" />;
    case "telegram":
      return <IoSendOutline className="text-secondary" />;
    default:
      return <IoInformationCircleOutline className="text-secondary" />;
  }
};

const getContactHref = (type: string, value: string) => {
  switch (type.toLowerCase()) {
    case "email":
      return `mailto:${value}`;
    case "website":
      return value.startsWith("http") ? value : `https://${value}`;
    case "instagram":
      return `https://instagram.com/${value.replace("@", "")}`;
    case "x":
    case "twitter":
      return `https://twitter.com/${value.replace("@", "")}`;
    case "facebook":
      return value.startsWith("http") ? value : `https://facebook.com/${value}`;
    case "linkedin":
      return value.startsWith("http")
        ? value
        : `https://linkedin.com/in/${value}`;
    case "telegram":
      return `https://t.me/${value.replace("@", "")}`;
    default:
      return value.startsWith("http") ? value : "#";
  }
};

const GearPalDetails = ({
  pal,
  onSelectItem,
  onAddItem,
  onEditItem,
  onDeleteItem,
}: GearPalDetailsProps) => {
  const { translateCategory, t } = useCategoryTranslation();
  const { user } = useUserStore();
  const [currentIndex, setCurrentIndex] = useState(0);

  const isOwner =
    user && user.email.toLowerCase() === pal.userEmail.toLowerCase();
  const items = pal.items || [];
  const visibleItems = items.slice(currentIndex, currentIndex + 2);

  const nextItems = () => {
    if (currentIndex + 2 < items.length) {
      setCurrentIndex(currentIndex + 2);
    }
  };

  const prevItems = () => {
    if (currentIndex - 2 >= 0) {
      setCurrentIndex(currentIndex - 2);
    }
  };

  return (
    <div className="space-y-8">
      <section>
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-secondary/20 rounded-lg">
              <DrumIcon size={20} className="text-secondary" />
            </div>
            <GearHeadings
              variant="h4"
              className="text-white uppercase tracking-widest text-xs"
            >
              {t("pal.items", "Articoli Disponibili")}
            </GearHeadings>
            {isOwner && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onAddItem?.();
                }}
                className="p-1 bg-secondary text-white rounded-full hover:bg-secondary-600 transition-all flex items-center justify-center shadow-lg"
                title={t("forms.addItem", "Aggiungi Articolo")}
              >
                <IoAddOutline size={16} />
              </button>
            )}
          </div>

          {items.length > 2 && (
            <div className="flex gap-2">
              <button
                onClick={prevItems}
                disabled={currentIndex === 0}
                className="p-1.5 rounded-full bg-white/5 text-white disabled:opacity-20 hover:bg-white/10 transition-colors border border-white/10"
              >
                <IoChevronBack size={16} />
              </button>
              <button
                onClick={nextItems}
                disabled={currentIndex + 2 >= items.length}
                className="p-1.5 rounded-full bg-white/5 text-white disabled:opacity-20 hover:bg-white/10 transition-colors border border-white/10"
              >
                <IoChevronForward size={16} />
              </button>
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4">
          {visibleItems.map((item, index) => (
            <GearItemCard
              key={index}
              item={item}
              translateCategory={translateCategory}
              onSelectItem={onSelectItem}
              onEditItem={onEditItem}
              onDeleteItem={onDeleteItem}
              isOwner={isOwner}
              t={t}
              city={pal.city}
              region={pal.region}
            />
          ))}
          {items.length === 0 && (
            <p className="text-white/40 text-center py-8 italic">
              {t("pages.profile.noGear", "Nessuna attrezzatura aggiunta.")}
            </p>
          )}
        </div>
      </section>

      {pal.contacts && pal.contacts.length > 0 && (
        <section className="bg-black/20 p-5 rounded-2xl border border-white/5">
          <h3 className="text-[10px] font-bold text-white/40 uppercase tracking-[0.2em] mb-4">
            {t("forms.contactInformation", "Informazioni di Contatto")}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {pal.contacts.map((contact, index) => (
              <a
                key={index}
                href={getContactHref(contact.type, contact.value)}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 p-3 bg-white/5 rounded-xl border border-white/5 hover:bg-white/10 hover:border-white/10 transition-all group"
              >
                <div className="p-2 bg-white/5 rounded-lg group-hover:bg-secondary/10 transition-colors">
                  {getContactIcon(contact.type)}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[9px] font-bold text-white/30 uppercase tracking-tight leading-none mb-1">
                    {contact.type}
                  </p>
                  <p className="text-sm font-medium text-white/90 truncate group-hover:text-secondary transition-colors">
                    {contact.value}
                  </p>
                </div>
              </a>
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

function GearItemCard({
  item,
  translateCategory,
  onSelectItem,
  onEditItem,
  onDeleteItem,
  isOwner,
  t,
  city,
  region,
}: {
  item: GearItem;
  translateCategory: any;
  onSelectItem: any;
  onEditItem?: (item: GearItem) => void;
  onDeleteItem?: (id: number) => void;
  isOwner: boolean;
  t: any;
  city: string;
  region: string;
}) {
  const imageUrl = item.images?.[0]?.linkImage || item.imageUrls?.[0];

  return (
    <div className="bg-white/5 border border-white/5 rounded-2xl overflow-hidden flex flex-col hover:border-white/10 transition-all group">
      <div className="p-2 pb-0">
        <div className="relative h-32 w-full rounded-xl overflow-hidden bg-white/5 flex items-center justify-center">
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={item.name}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
          ) : (
            <IoMusicalNotesOutline size={32} className="text-white/10" />
          )}
          <div className="absolute top-2 right-2 px-2 py-0.5 bg-black/60 backdrop-blur-md rounded-full border border-white/10 flex">
            <span className="text-[10px] font-bold text-secondary uppercase">
              {item.noleggio !== "0.00"
                ? `€${item.noleggio}${t("common.perDay")}`
                : t("common.free")}
            </span>
          </div>
        </div>
      </div>
      <div className="p-4 flex flex-col flex-grow">
        <div className="flex justify-between items-start mb-1">
          <h4 className="text-white font-bold text-base leading-tight line-clamp-1 group-hover:text-secondary transition-colors">
            {item.name}
          </h4>
        </div>
        <p className="text-white/40 text-[10px] mb-4">
          {translateCategory(item.category)} • {city}, {region}
        </p>
        {isOwner ? (
          <div className="flex gap-2">
            <GearButton
              variant="secondary"
              onClick={() => onEditItem?.(item)}
              className="flex-1  !text-[10px] !rounded-full uppercase font-bold"
            >
              {t("common.edit", "Modifica")}
            </GearButton>
            <button
              onClick={() => {
                if (window.confirm(t("common.confirmDelete", "Sei sicuro?"))) {
                  onDeleteItem?.(item.id as number);
                }
              }}
              className="flex-1 text-[10px] rounded-full uppercase font-bold bg-white/5 text-red-400 border border-white/10 hover:bg-red-500/10 transition-colors"
            >
              {t("common.delete", "Elimina")}
            </button>
          </div>
        ) : (
          <GearButton
            variant="secondary"
            onClick={() => onSelectItem(item)}
            className="w-full !py-2 !text-xs !rounded-full"
          >
            {t("booking.view", "Visualizza")}
          </GearButton>
        )}
      </div>
    </div>
  );
}

export default GearPalDetails;
